import React, { useEffect, useState } from "react";
import {
  Box,
  Sheet,
  Table,
  Button,
  Typography,
  Stack,
} from "@mui/joy";
import { CalendarMonthOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  LoadingView,
  PageTitle,
  DatePickerModal,
  parseDate,
  toInputDate,
  formatRangeLabel,
} from "../../../components";
import { connect, useDispatch } from "react-redux";

import {
  getAgentCommissionsRequest,
  getAgentCommissionsReset,
  getAgentCommissionSummaryRequest,
  getAgentCommissionSummaryReset,
  getAgentCommissionsByDateRequest,
  getAgentCommissionsByDateReset,
} from "../../../store/actions";

import { useLocation } from "react-router-dom";
import { STATUS } from "../../../constant";

const PRESETS = [
  { label: "This Week", key: "week" },
  { label: "This Month", key: "month" },
];

function formatDate(value) {
  if (!value) return "-";
  const date = parseDate(value);
  if (!date) return value;
  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function formatMonthLabel(value) {
  if (!value) return "-";
  const parts = String(value).split("-");
  if (parts.length < 2) return value;
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  const date = new Date(year, month, 1);
  if (isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en", {
    month: "long",
    year: "numeric",
  }).format(date);
}

const AgentCommissionsPage = ({
  loginResult,

  agentCommissionsStatus,
  agentCommissionsResult,
  agentCommissionsErrorMessage,

  agentCommissionSummaryStatus,
  agentCommissionSummaryResult,
  agentCommissionSummaryErrorMessage,

  agentCommissionsByDateStatus,
  agentCommissionsByDateResult,
  agentCommissionsByDateErrorMessage,
}) => {
  const dispatch = useDispatch();
  const { state } = useLocation();

  const { agent_id } = state;

  const [commissionList, setCommissionList] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activePreset, setActivePreset] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  useEffect(() => {
    setCommissionList([]);
    dispatch(
      getAgentCommissionsRequest(loginResult.token, { wakala_id: agent_id }),
    );
    dispatch(
      getAgentCommissionSummaryRequest(loginResult.token, {
        wakala_id: agent_id,
      }),
    );
  }, [agent_id, dispatch, loginResult]);

  useEffect(() => {
    if (agentCommissionsStatus === STATUS.SUCCESS) {
      setCommissionList(agentCommissionsResult || []);
      setTimeout(() => {
        dispatch(getAgentCommissionsReset());
      }, 2000);
    } else if (agentCommissionsStatus === STATUS.ERROR) {
      toast.error(agentCommissionsErrorMessage);
      dispatch(getAgentCommissionsReset());
    }

    if (agentCommissionsByDateStatus === STATUS.SUCCESS) {
      setCommissionList(agentCommissionsByDateResult || []);
      setTimeout(() => {
        dispatch(getAgentCommissionsByDateReset());
      }, 2000);
    } else if (agentCommissionsByDateStatus === STATUS.ERROR) {
      toast.error(agentCommissionsByDateErrorMessage);
      dispatch(getAgentCommissionsByDateReset());
    }

    if (agentCommissionSummaryStatus === STATUS.ERROR) {
      toast.error(agentCommissionSummaryErrorMessage);
      dispatch(getAgentCommissionSummaryReset());
    }
  });

  const applyPreset = (preset) => {
    setActivePreset(preset.label);
    setCommissionList([]);
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (preset.key === "week") {
      start.setDate(end.getDate() - end.getDay());
    } else if (preset.key === "month") {
      start.setDate(1);
    }
    setStartDate(toInputDate(start));
    setEndDate(toInputDate(end));
    const data = {
      wakala_id: agent_id,
      start_date: toInputDate(start),
      end_date: toInputDate(end),
    };
    dispatch(getAgentCommissionsByDateRequest(loginResult.token, data));
  };

  const filterByDate = () => {
    setActivePreset(null);
    setCommissionList([]);
    setStartOpen(false);
    setEndOpen(false);
    const data = {
      wakala_id: agent_id,
      start_date: startDate || null,
      end_date: endDate || null,
    };
    dispatch(getAgentCommissionsByDateRequest(loginResult.token, data));
  };

  const clearFilter = () => {
    setStartDate("");
    setEndDate("");
    setActivePreset(null);
    setCommissionList([]);
    dispatch(
      getAgentCommissionsRequest(loginResult.token, { wakala_id: agent_id }),
    );
  };

  const handleSelectStart = (value) => {
    setStartDate(value);
    setActivePreset(null);
  };

  const handleSelectEnd = (value) => {
    setEndDate(value);
    setActivePreset(null);
  };

  const checkLoading = () => {
    if (
      agentCommissionsStatus === STATUS.LOADING ||
      agentCommissionSummaryStatus === STATUS.LOADING ||
      agentCommissionsByDateStatus === STATUS.LOADING
    ) {
      return true;
    } else {
      return false;
    }
  };

  const summary = agentCommissionSummaryResult;

  return (
    <Box>
      <PageTitle title={"Agent Commissions"} />

      <LoadingView loading={checkLoading()} />

      {/* Summary */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Sheet
          variant="outlined"
          sx={{ p: 2, borderRadius: "md", flex: 1, boxShadow: "sm" }}
        >
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Total Commission
          </Typography>
          <Typography level="h3">
            {summary ? summary.total_commission : "-"}
          </Typography>
        </Sheet>
        <Sheet
          variant="outlined"
          sx={{ p: 2, borderRadius: "md", flex: 1, boxShadow: "sm" }}
        >
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Transactions
          </Typography>
          <Typography level="h3">
            {summary ? summary.transaction_count : "-"}
          </Typography>
        </Sheet>
        <Sheet
          variant="outlined"
          sx={{ p: 2, borderRadius: "md", flex: 1, boxShadow: "sm" }}
        >
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Period
          </Typography>
          <Typography level="h3">
            {summary ? formatMonthLabel(summary.month) : "-"}
          </Typography>
        </Sheet>
      </Stack>

      {/* Date filter */}
      <Sheet
        variant="outlined"
        sx={{ borderRadius: "md", p: 2, mb: 3, boxShadow: "sm" }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "flex-end" }}
          flexWrap="wrap"
        >
          <Stack spacing={0.5} sx={{ width: "100%", flex: 1, minWidth: 180 }}>
            <Typography level="body-sm">Start date</Typography>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<CalendarMonthOutlined />}
              onClick={() => setStartOpen(true)}
              sx={{ justifyContent: "flex-start", width: "100%" }}
            >
              {startDate ? formatRangeLabel(startDate) : "Select start"}
            </Button>
          </Stack>
          <Stack spacing={0.5} sx={{ width: "100%", flex: 1, minWidth: 180 }}>
            <Typography level="body-sm">End date</Typography>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<CalendarMonthOutlined />}
              onClick={() => setEndOpen(true)}
              sx={{ justifyContent: "flex-start", width: "100%" }}
            >
              {endDate ? formatRangeLabel(endDate) : "Select end"}
            </Button>
          </Stack>
          <Button color="primary" onClick={filterByDate} disabled={!startDate || !endDate}>
            Apply
          </Button>
          <Button color="neutral" variant="outlined" onClick={clearFilter}>
            Clear
          </Button>
        </Stack>

        <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 1.5 }}>
          Quick select:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
          {PRESETS.map((preset) => (
            <Button
              key={preset.label}
              size="sm"
              variant={activePreset === preset.label ? "solid" : "soft"}
              color={activePreset === preset.label ? "primary" : "neutral"}
              onClick={() => applyPreset(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </Stack>
      </Sheet>

      <DatePickerModal
        open={startOpen}
        value={startDate}
        onSelect={handleSelectStart}
        onClose={() => setStartOpen(false)}
      />
      <DatePickerModal
        open={endOpen}
        value={endDate}
        onSelect={handleSelectEnd}
        onClose={() => setEndOpen(false)}
      />

      {/* Commissions list */}
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level2)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th>#</th>
              <th>Amount</th>
              <th>Agent Code</th>
              <th>OP Reference</th>
              <th>Trans Date</th>
            </tr>
          </thead>
          <tbody>
            {commissionList.length > 0 ? (
              commissionList.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{row.amount}</td>
                  <td>{row.agent_code}</td>
                  <td>{row.op_reference}</td>
                  <td>{formatDate(row.trans_date)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  No commissions found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Sheet>
    </Box>
  );
};

const mapStateToProps = ({ agent, auth }) => {
  const { otpResult: loginResult } = auth;
  const {
    agentCommissionsStatus,
    agentCommissionsResult,
    agentCommissionsErrorMessage,

    agentCommissionSummaryStatus,
    agentCommissionSummaryResult,
    agentCommissionSummaryErrorMessage,

    agentCommissionsByDateStatus,
    agentCommissionsByDateResult,
    agentCommissionsByDateErrorMessage,
  } = agent;

  return {
    loginResult,

    agentCommissionsStatus,
    agentCommissionsResult,
    agentCommissionsErrorMessage,

    agentCommissionSummaryStatus,
    agentCommissionSummaryResult,
    agentCommissionSummaryErrorMessage,

    agentCommissionsByDateStatus,
    agentCommissionsByDateResult,
    agentCommissionsByDateErrorMessage,
  };
};

export default connect(mapStateToProps, {})(AgentCommissionsPage);
